import {
  FreeSoloAutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useQuery } from '@apollo/client'
import { Box, Button, MenuItem, Typography } from '@material-ui/core'
import { useState } from 'react'

import SectionTitle from '../../components/SectionTitle'
import * as notificationGraphql from '../../graphql/notifications'
import { idempotencyKey, t } from './shared'
import useStyles from './styles/TemplateForm.styles'
import { ResourceRow } from './types'

/**
 * The GraphQL enum, not the service's storage spelling.
 *
 * Three representations exist: `IN_APP` in GraphQL, `DELIVERY_CHANNEL_IN_APP`
 * on the protobuf wire, `INAPP` in notification-service's Postgres. The BFF maps
 * the first two (notification_inbox_helpers.go) and the service maps the third,
 * so this layer must use the GraphQL one.
 */
const CHANNELS = ['IN_APP', 'SMS', 'EMAIL'] as const

interface TemplateFormProps {
  /** Prefilled when editing a row; empty for a new template. */
  initial?: ResourceRow
  saving: boolean
  onSubmit: (template: ResourceRow) => void
}

/**
 * A real form for notification templates, replacing the JSON textarea (§6).
 *
 * Templates are the first of the three flat resources to convert. Every field
 * maps to an existing portal component — `body` is free text, so a textarea is
 * the CORRECT control for it rather than a fallback, which is what separates
 * this resource from Rules (whose fields are CEL source).
 */
export default function TemplateForm({
  initial,
  saving,
  onSubmit
}: TemplateFormProps) {
  const classes = useStyles()
  // Lazy initializer: the fallback generates an id, and the eager form would
  // run that generator on every render and discard the result.
  const [draft, setDraft] = useState<ResourceRow>(
    () =>
      initial ?? {
        // `id` is ID! on the input, required even when creating, and nothing
        // downstream supplies one — a new template submitted without it fails
        // GraphQL validation before reaching the service. Generated the same way
        // SubscriptionForm does rather than inventing a second generator.
        id: idempotencyKey(),
        categoryKey: '',
        channel: 'IN_APP',
        locale: 'fa',
        version: 1,
        subject: '',
        body: '',
        variables: [],
        active: true
      }
  )

  // The category picker's options. Templates is an admin tab, so the operator
  // reaching this form already has the read scope this query needs.
  const { data: categoryData } = useQuery(
    notificationGraphql.NOTIFICATION_ADMIN_CATEGORIES_QUERY,
    { variables: { pageSize: 100 } }
  )
  const categories: ResourceRow[] =
    categoryData?.notificationAdminCategories?.categories ?? []

  const set = (field: string, value: unknown) =>
    setDraft((current) => ({ ...current, [field]: value }))

  const channel = String(draft.channel ?? '')
  // Subject is EMAIL-only: an in-app card and an SMS have no subject line, and
  // the seeded templates carry NULL there. Showing the field for every channel
  // invites an operator to fill in something that is never rendered.
  const showsSubject = channel === 'EMAIL'

  return (
    <Box mt={2}>
      <SectionTitle title={t('Create or update template')} />
      <div className={classes.grid}>
        {/* SelectWithLabel, not AutocompleteWithLabel: the library's
            autocomplete is typed AutocompleteProps<..., true, ...> — always
            multiple — so it cannot express a single choice. Select is the
            portal's Standard for dropdowns anyway (41 screens). */}
        {/* SelectWithLabel names its control since @adotel/components 1.3.0
            (labelId -> aria-labelledby; the id alone could not do it, because
            MUI puts it on a hidden input while a div[role="button"] takes the
            interaction). No workaround is needed here any more. */}
        <SelectWithLabel
          id="template-category"
          name="template-category"
          label={t('Category')}
          value={String(draft.categoryKey ?? '')}
          onChange={(event: { target: { value: unknown } }) =>
            set('categoryKey', event.target.value)
          }>
          {categories.map((category) => (
            <MenuItem key={String(category.key)} value={String(category.key)}>
              {String(category.key)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <SelectWithLabel
          id="template-channel"
          name="template-channel"
          label={t('Channel')}
          value={channel}
          onChange={(event: { target: { value: unknown } }) =>
            set('channel', event.target.value)
          }>
          {CHANNELS.map((option) => (
            <MenuItem key={option} value={option}>
              {t(option)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <InputWithLabel
          id="template-locale"
          name="template-locale"
          label={t('Locale')}
          value={String(draft.locale ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('locale', event.currentTarget.value)
          }
        />
        <InputWithLabel
          id="template-version"
          name="template-version"
          label={t('Version')}
          type="number"
          value={String(draft.version ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('version', Number(event.currentTarget.value))
          }
        />
      </div>

      {showsSubject && (
        <InputWithLabel
          id="template-subject"
          name="template-subject"
          fullWidth
          label={t('Subject')}
          value={String(draft.subject ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set('subject', event.currentTarget.value)
          }
        />
      )}

      <TextAreaWithLabel
        fullWidth
        rows={6}
        label={t('Body')}
        noHelperText
        value={String(draft.body ?? '')}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
          set('body', event.currentTarget.value)
        }
      />
      {/* The body is a Go template. Naming its placeholders is the whole point
          of the variables list, so they sit together. */}
      <Typography variant="caption" color="textSecondary">
        {t('Reference variables as {placeholder}.', {
          placeholder: '{{.variable_name}}'
        })}
      </Typography>

      {/* The free-solo variant, because a template's variables are whatever
          its body references — there is no fixed option list to pick from. */}
      <FreeSoloAutocompleteWithLabel
        label={t('Variables')}
        options={[]}
        value={(draft.variables as string[]) ?? []}
        onChange={(_event: unknown, value: string[]) => set('variables', value)}
        // The component requires a pattern, and that is a gain over the JSON
        // editor rather than a chore: a variable name must be a Go template
        // identifier, and a typo there fails at render time with the
        // placeholder left in the message a user receives.
        regexPattern={/^[a-z][a-z0-9_]*$/}
        message={t('Use lowercase letters, digits and underscores.')}
      />

      <Button
        variant="contained"
        color="primary"
        disabled={saving}
        className={classes.submit}
        onClick={() => onSubmit(draft)}>
        {saving ? t('Saving…') : t('Save template')}
      </Button>
    </Box>
  )
}
